/**
 * Sticky Product Gallery Functionality
 * Makes the left image gallery sticky while scrolling through product information
 */

class StickyProductGallery {
  constructor() {
    this.mediaWrapper = null;
    this.infoWrapper = null;
    this.isSticky = false;
    this.scrollThreshold = 100;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupStickyGallery());
    } else {
      this.setupStickyGallery();
    }
  }

  setupStickyGallery() {
    // Find the product media wrapper and info wrapper
    this.mediaWrapper = document.querySelector('.product__media-wrapper');
    this.infoWrapper = document.querySelector('.product__info-wrapper');
    
    if (!this.mediaWrapper || !this.infoWrapper) {
      return; // Not on a product page
    }

    // Only apply on desktop/tablet
    if (window.innerWidth < 750) {
      return;
    }

    this.setupStickyBehavior();
    this.setupResizeHandler();
    this.setupScrollHandler();
    this.setupIntersectionObserver();
  }

  setupStickyBehavior() {
    // Add smooth transition for sticky behavior
    this.mediaWrapper.style.transition = 'all 0.3s ease-in-out';
    
    // Ensure proper z-index stacking
    this.mediaWrapper.style.zIndex = '1';
    
    // Add a class for additional styling if needed
    this.mediaWrapper.classList.add('sticky-gallery-enabled');
    
    // Set initial state
    this.updateStickyState();
  }

  setupResizeHandler() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  setupScrollHandler() {
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (scrollTimeout) {
        return; // Throttle scroll events
      }
      
      scrollTimeout = setTimeout(() => {
        this.handleScroll();
        scrollTimeout = null;
      }, 10);
    });
  }

  setupIntersectionObserver() {
    // Use Intersection Observer for better performance
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.enableSticky();
        } else {
          this.disableSticky();
        }
      });
    }, options);

    // Observe the product section
    const productSection = document.querySelector('.product__details-wrapper');
    if (productSection) {
      observer.observe(productSection);
    }
  }

  handleResize() {
    // Disable sticky on mobile
    if (window.innerWidth < 750) {
      this.disableSticky();
      this.mediaWrapper.classList.remove('sticky-gallery-enabled');
    } else {
      this.mediaWrapper.classList.add('sticky-gallery-enabled');
      this.updateStickyState();
    }
  }

  handleScroll() {
    if (window.innerWidth < 750) {
      return; // Don't handle scroll on mobile
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Check if we should enable/disable sticky based on scroll position
    if (scrollTop > this.scrollThreshold && !this.isSticky) {
      this.enableSticky();
    } else if (scrollTop <= this.scrollThreshold && this.isSticky) {
      this.disableSticky();
    }

    // Handle bottom boundary
    this.handleBottomBoundary();
  }

  updateStickyState() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > this.scrollThreshold) {
      this.enableSticky();
    } else {
      this.disableSticky();
    }
  }

  enableSticky() {
    if (this.isSticky) return;
    
    this.isSticky = true;
    this.mediaWrapper.classList.add('is-sticky');
    
    // Adjust top position based on screen size
    const topOffset = window.innerWidth >= 990 ? '30px' : '20px';
    this.mediaWrapper.style.top = topOffset;
    
    // Add shadow and visual feedback
    this.mediaWrapper.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
    this.mediaWrapper.style.borderRadius = '8px';
    this.mediaWrapper.style.background = '#fff';
  }

  disableSticky() {
    if (!this.isSticky) return;
    
    this.isSticky = false;
    this.mediaWrapper.classList.remove('is-sticky');
    this.mediaWrapper.style.top = '';
    this.mediaWrapper.style.boxShadow = '';
    this.mediaWrapper.style.borderRadius = '';
    this.mediaWrapper.style.background = '';
  }

  handleBottomBoundary() {
    const mediaWrapperRect = this.mediaWrapper.getBoundingClientRect();
    const infoWrapperRect = this.infoWrapper.getBoundingClientRect();
    
    // If the info wrapper is shorter than the media wrapper, 
    // adjust the sticky behavior to prevent the media from sticking beyond the content
    if (infoWrapperRect.height < mediaWrapperRect.height) {
      const bottomBoundary = infoWrapperRect.bottom - mediaWrapperRect.height;
      const currentTop = window.pageYOffset + mediaWrapperRect.top;
      
      if (currentTop > bottomBoundary) {
        this.mediaWrapper.style.top = `${bottomBoundary - window.pageYOffset}px`;
      }
    }
  }

  // Method to handle gallery layout changes
  handleGalleryLayoutChange() {
    // Recalculate sticky behavior when gallery layout changes
    setTimeout(() => {
      this.updateStickyState();
    }, 100);
  }
}

// Initialize sticky product gallery
document.addEventListener('DOMContentLoaded', () => {
  new StickyProductGallery();
});

// Also initialize on page load for dynamic content
window.addEventListener('load', () => {
  if (!window.stickyProductGallery) {
    window.stickyProductGallery = new StickyProductGallery();
  }
});

// Handle dynamic content changes (like variant changes)
document.addEventListener('variant:change', () => {
  if (window.stickyProductGallery) {
    window.stickyProductGallery.handleGalleryLayoutChange();
  }
});

// Handle gallery navigation
document.addEventListener('gallery:change', () => {
  if (window.stickyProductGallery) {
    window.stickyProductGallery.handleGalleryLayoutChange();
  }
}); 